'use client';

import React, { useState } from 'react';
import { Edit, BarChart3, ExternalLink, CalendarCheck, Users, Clock } from 'lucide-react';
import { EventRecord } from './mockEvents';
import { toast } from 'sonner';

interface Props {
  event: EventRecord;
}

const statusStyle: Record<string, string> = {
  active: 'event-active',
  planned: 'event-planned',
  completed: 'event-completed',
  archived: 'event-archived',
};

const typeColor: Record<string, string> = {
  'Conference': 'bg-indigo-50 text-indigo-700',
  'Trade Show': 'bg-blue-50 text-blue-700',
  'Expo': 'bg-cyan-50 text-cyan-700',
  'Summit': 'bg-violet-50 text-violet-700',
  'Forum': 'bg-purple-50 text-purple-700',
  'Exhibition': 'bg-sky-50 text-sky-700',
};

function formatShortDate(d: string): string {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

export default function EventListRow({ event }: Props) {
  const [hovered, setHovered] = useState(false);
  const meetingRate = event.meetingRequests > 0
    ? Math.round((event.meetingsConfirmed / event.meetingRequests) * 100)
    : 0;
  const pipelinePct = event.totalContacts > 0
    ? Math.round((event.linkedinConnected / event.totalContacts) * 100)
    : 0;

  return (
    <tr
      className="table-row"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Event name */}
      <td className="table-td">
        <div>
          <p className="text-xs font-semibold text-foreground">{event.name}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5 truncate max-w-[200px]">{event.venue}</p>
        </div>
      </td>

      {/* Type */}
      <td className="table-td">
        <span className={`badge ${typeColor[event.type] ?? 'bg-gray-100 text-gray-600'}`}>
          {event.type}
        </span>
      </td>

      {/* Dates */}
      <td className="table-td">
        <span className="text-xs font-mono text-muted-foreground">
          {formatShortDate(event.startDate)} – {formatShortDate(event.endDate)}
        </span>
      </td>

      {/* Location */}
      <td className="table-td">
        <span className="text-xs text-foreground">{event.city}, {event.country}</span>
      </td>

      {/* Status */}
      <td className="table-td">
        <span className={`badge ${statusStyle[event.status]}`}>
          {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
        </span>
      </td>

      {/* Contacts */}
      <td className="table-td">
        <div className="flex items-center gap-1.5">
          <Users size={12} className="text-muted-foreground" />
          <span className="text-xs font-mono font-semibold text-foreground">{event.totalContacts}</span>
          <span className="text-[10px] text-muted-foreground">({event.highPriority} high)</span>
        </div>
      </td>

      {/* Pipeline mini bar */}
      <td className="table-td">
        <div className="flex items-center gap-2 min-w-[100px]">
          <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
            <div
              className="h-full rounded-full bg-sky-400 transition-all duration-500"
              style={{ width: `${pipelinePct}%` }}
            />
          </div>
          <span className="text-[10px] font-mono text-muted-foreground w-8 text-right">{pipelinePct}%</span>
        </div>
      </td>

      {/* Meetings */}
      <td className="table-td">
        <div className="flex items-center gap-1.5">
          <CalendarCheck size={12} className="text-emerald-500" />
          <span className="text-xs font-mono font-semibold text-emerald-700">{event.meetingsConfirmed}</span>
          <span className="text-[10px] text-muted-foreground">confirmed</span>
          {event.meetingRequests > 0 && (
            <span className="text-[10px] text-muted-foreground">· {meetingRate}% rate</span>
          )}
        </div>
      </td>

      {/* Countdown */}
      <td className="table-td">
        {event.daysUntil > 0 ? (
          <div className="flex items-center gap-1.5">
            <Clock size={12} className="text-amber-500" />
            <span className="text-xs font-mono font-semibold text-amber-700">{event.daysUntil}d</span>
          </div>
        ) : (
          <span className="text-[11px] text-muted-foreground">
            {Math.abs(event.daysUntil)}d ago
          </span>
        )}
      </td>

      {/* Actions */}
      <td className="table-td text-right">
        <div className={`flex items-center justify-end gap-0.5 transition-opacity duration-150 ${hovered ? 'opacity-100' : 'opacity-0'}`}>
          <button
            title="View dashboard"
            onClick={() => toast.info(`Opening ${event.name} dashboard`)}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all active:scale-95"
          >
            <BarChart3 size={13} />
          </button>
          <button
            title="Edit event"
            onClick={() => toast.info(`Editing ${event.name}`)}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all active:scale-95"
          >
            <Edit size={13} />
          </button>
          <button
            title="Visit website"
            onClick={() => toast.info(`Opening ${event.website}`)}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all active:scale-95"
          >
            <ExternalLink size={13} />
          </button>
        </div>
      </td>
    </tr>
  );
}